<template>
  <div class="cf-flex cf-flex--column cf-flex--gap-16">
    <template v-if="menu.loadStatus === 'error'">
      <p class="message message--danger">
        Oops! We weren't able to load the menu. Please try again later or select a different location.
      </p>
    </template>

    <template v-else>
      <cf-menu-list-filter
        v-model:search-term="searchTerm"
        :is-read-only="isReadOnly"
        :is-category-card-layout="isCategoryCardLayout"
        @scroll-to-category="handleOpenCategoryClick"
      />

      <slot name="menu-list-page-banner" />

      <template v-if="bannerContent.bannerUrl && bannerContent.bannerImage">
        <a :href="bannerContent.bannerUrl">
          <img
            class="cf-image cf-image--banner"
            :alt="merchant.name"
            :src="bannerContent.bannerImage"
            onerror="this.src='/images/image-not-found.png';"
          >
        </a>
      </template>
      <template v-else-if="bannerContent.bannerImage">
        <img
          class="cf-image cf-image--banner"
          :alt="merchant.name"
          :src="bannerContent.bannerImage"
          onerror="this.src='/images/image-not-found.png';"
        >
      </template>

      <cf-menu-list-filter-status v-if="hasSelectedMenuFilters" />

      <cf-menu-list-search-filter-results
        v-if="searchTerm"
        :is-read-only="isReadOnly"
        :search-term="searchTerm"
        :search-results="menuItemSearchResults"
      />

      <cf-menu-list-category-cards
        v-else-if="isCategoryCardLayout"
        :categories="menu.filteredCategories"
        :open-category-ids="openCategoryIds"
        :is-read-only="isReadOnly"
        @open-menu-list-category="handleOpenCategoryClick"
      />

      <div
        v-else
        :class="['cf-menu-list cf-menu-list--default']"
      >
        <cf-menu-list-category
          v-for="(category, categoryIndex) in menu.filteredCategories"
          :key="category.categoryId"
          :category="category"
          :category-index="categoryIndex"
          :open-category-ids="openCategoryIds"
          :is-read-only="isReadOnly"
          @open-menu-list-category="handleOpenCategoryClick"
        />
      </div>
    </template>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import { generateCategoryHash, setBrowserHash } from '@/helpers/menu-hash-helper';

  import CfMenuListFilter from './cf-menu-list-filter/index.vue';
  import CfMenuListFilterStatus from './cf-menu-list-filter-status/index.vue';
  import CfMenuListSearchFilterResults from './cf-menu-list-search-filter-results/index.vue';
  import CfMenuListCategoryCards from './cf-menu-list-category-cards/index.vue';
  import CfMenuListCategory from './cf-menu-list-category/index.vue';


  export default {
    name: 'CfMenuList',

    components: {
      CfMenuListFilter,
      CfMenuListFilterStatus,
      CfMenuListSearchFilterResults,
      CfMenuListCategoryCards,
      CfMenuListCategory
    },

    props: {
      itemRouteName: {
        type: String,
        default: null
      },

      isReadOnly: {
        type: Boolean,
        default: false
      },

      isCategoryCardLayout: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        openCategoryIds: [],
        searchTerm: ''
      };
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('favoriteItems', ['favoriteItems']),
      ...mapGetters('menu', ['menu', 'filterAttributes']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('session', ['orderDetails', 'session']),

      bannerContent() {
        return {
          bannerImage: this.content.MenuBanner?.metadata?.bannerAdImageUrl,
          bannerUrl: this.content.MenuBanner?.metadata?.bannerAdTargetUrl
        };
      },

      hasSelectedMenuFilters() {
        return this.filterAttributes.selected.length > 0;
      },

      menuItemSearchResults() {
        const results = [];
        const resultMenuItemIds = [];
        const searchTerm = this.sanitizeString(this.searchTerm);

        this.menu.filteredCategories.forEach((category) => {
          if (category.categoryTemplate === 'FavoritesCategoryTemplate') return;

          const isHiddenCategory = category.statementTemplate === 'HiddenTemplate';

          category.menuItems.forEach((menuItem) => {
            const menuItemDisplayName = this.sanitizeString(menuItem.displayName);

            const menuItemDescription = this.sanitizeString(menuItem.descriptionText);

            const isSearchTagMatch = this.checkIsSearchTagMatch(menuItem, searchTerm, resultMenuItemIds);

            const isMenuItemNameMatch = menuItemDisplayName.indexOf(searchTerm) > -1 && resultMenuItemIds.indexOf(menuItem.menuItemId) === -1;

            const isMenuItemDescriptionMatch = menuItemDescription?.indexOf(searchTerm) > -1 && resultMenuItemIds.indexOf(menuItem.menuItemId) === -1;

            if (!isHiddenCategory && (isMenuItemNameMatch || isMenuItemDescriptionMatch || isSearchTagMatch)) {
              results.push(menuItem);

              resultMenuItemIds.push(menuItem.menuItemId);
            }
          });
        });

        return results;
      },

      isMenuFilterEnabled() {
        return this.isMenuSearchEnabled || this.isFilterByItemAttributesEnabled;
      },

      isMenuSearchEnabled() {
        return this.merchant.features['enable-menu-search'];
      },

      isFilterByItemAttributesEnabled() {
        /** all available menu item attributes that are available for filtering */
        return this.filterAttributes.all.length > 0;
      }
    },

    mounted() {
      this.onComponentMounted();
    },

    methods: {
      onComponentMounted() {
        this.$store.dispatch('favoriteItems/pruneUnfavoritedItems');

        if (this.menu.categories.length === 1 || this.merchant.options.defaultCategoriesExpanded) {
          this.openCategoryIds = this.menu.categories.map(c => c.categoryId);
        }
        else if (this.menu.categories.length) {
          const [firstCategory] = this.menu.categories;

          if (firstCategory) {
            this.openCategoryIds.push(firstCategory.categoryId);
          }
        }
      },

      handleOpenCategoryClick({ categoryId, doNotClose }) {
        const category = this.menu.filteredCategories.find(cat => cat.categoryId === categoryId);

        if (category && category.displayName) {
          const categoryHash = generateCategoryHash(category.displayName);
          setBrowserHash(categoryHash);
        }

        const categoryIndex = this.openCategoryIds.indexOf(categoryId);

        const categoryWasOpen = categoryIndex > -1;

        if (doNotClose && categoryWasOpen) {
          return;
        }

        if (categoryWasOpen) {
          this.openCategoryIds.splice(categoryIndex, 1);
        }

        else {
          this.openCategoryIds.push(categoryId);
        }
      },

      checkIsSearchTagMatch(menuItem, searchTerm, resultMenuItemIds) {
        const isMatch = menuItem.attributes.some((item) => {
          const isSearchTag = item.type === 'SearchTag';
          const itemTagName = this.sanitizeString(item.name);

          return isSearchTag && itemTagName.indexOf(searchTerm) > -1 && resultMenuItemIds.indexOf(menuItem.menuItemId) === -1;
        });

        return isMatch;
      },

      sanitizeString(str) {
        return str?.toLowerCase().replace(/[^a-zA-Z0-9\s]/, '').trim();
      }
    }
  };
</script>
